import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { dayLabel, isOver, requestDate, requestWindowLabel } from '@kronos/domain'
import type { Instant } from '@kronos/domain'
import { repository } from '../../data'
import type { ComeInRequestView } from '../../data/schedule'
import { problemOf } from '../hours/time'
import { Badge, Panel, Section } from '../primitives'
import { STATE_LABEL, STATE_TONE, useScheduleChanged } from './shared'

/**
 * Requests still waiting for an answer, soonest first, each with Cancel.
 * Nothing at all when there are none: an empty "awaiting" list is the good
 * case and does not need announcing.
 */
export function PendingList({ pending, timeZone }: { pending: readonly ComeInRequestView[]; timeZone: string }): JSX.Element | null {
  if (pending.length === 0) return null
  return (
    <Section label={`Awaiting an answer · ${pending.length}`}>
      <Panel>
        <div className="row-list">
          {pending.map((r) => (
            <RequestLine key={r.id} request={r} timeZone={timeZone} withName />
          ))}
        </div>
      </Panel>
    </Section>
  )
}

/**
 * Accepted, then "can't make it": the engineer was counting on these, so they
 * lead the page until the day has passed. No action — the slot is open again,
 * and the grid is where to ask someone else.
 */
export function BackedOutList({ requests, timeZone }: { requests: readonly ComeInRequestView[]; timeZone: string }): JSX.Element | null {
  if (requests.length === 0) return null
  return (
    <Section label={`Can’t make it · ${requests.length}`}>
      <Panel>
        <div className="row-list">
          {requests.map((r) => (
            <div key={r.id} className="row-item backed-out">
              <div className="row-item__main">
                <span className="row-item__title">{backedOutLine(r, timeZone)}</span>
                <span className="row-item__meta">
                  {r.note || 'No note'}
                  {r.reply ? ` · “${r.reply}”` : ''}
                </span>
              </div>
              <div className="row-item__side">
                <Badge tone="warning">Backed out</Badge>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </Section>
  )
}

/** "Alex can't make Thu 29 Oct · 09:00–12:00". */
export function backedOutLine(r: ComeInRequestView, timeZone: string): string {
  return `${r.technicianName} can’t make ${dayLabel(requestDate(r, timeZone))} · ${requestWindowLabel(r, timeZone)}`
}

/** The requests already on one person's day, inside the request form. */
export function DayRequests({ requests, timeZone, now }: { requests: readonly ComeInRequestView[]; timeZone: string; now: string }): JSX.Element {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="field-label">Already on this day</span>
      <div className="panel">
        <div className="row-list">
          {requests.map((r) => (
            <RequestLine key={r.id} request={r} timeZone={timeZone} cancellable={!isOver(r, now as Instant)} />
          ))}
        </div>
      </div>
    </div>
  )
}

function RequestLine({
  request,
  timeZone,
  withName = false,
  cancellable = true,
}: {
  request: ComeInRequestView
  timeZone: string
  withName?: boolean
  cancellable?: boolean
}): JSX.Element {
  const changed = useScheduleChanged()
  const [problem, setProblem] = useState('')
  const cancel = useMutation({
    mutationFn: () => repository.cancelRequest(request.id),
    onSuccess: changed,
    onError: (e) => setProblem(problemOf(e)),
  })
  const date = requestDate(request, timeZone)
  const canCancel = cancellable && (request.state === 'pending' || request.state === 'accepted')

  return (
    <div className="row-item">
      <div className="row-item__main">
        <span className="row-item__title">
          {withName ? `${request.technicianName} · ` : ''}
          {dayLabel(date)} · <span className="figure">{requestWindowLabel(request, timeZone)}</span>
        </span>
        <span className="row-item__meta">
          {request.note || 'No note'}
          {request.reply ? ` · “${request.reply}”` : ''}
          {problem ? <span className="critical"> · {problem}</span> : null}
        </span>
      </div>
      <div className="row-item__side">
        {withName ? null : <Badge tone={STATE_TONE[request.state]}>{STATE_LABEL[request.state]}</Badge>}
        {canCancel ? (
          <button className="danger booked-out" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            Cancel request
          </button>
        ) : null}
      </div>
    </div>
  )
}
